import { createLazyApiInstance } from '../utils/axiosSetup';

// Create lazy-loaded axios instance (waits for AuthContext callbacks to be registered)
const api = createLazyApiInstance();

// Donors API Service
export const donorsService = {
  // Get all donors with pagination and filtering
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.year) queryParams.append('year', params.year);
    if (params.search) queryParams.append('search', params.search);
    if (params.includeDeleted) queryParams.append('includeDeleted', params.includeDeleted);

    return api.get(`/donors?${queryParams.toString()}`);
  },

  // Get single donor with full details
  get: (id) => api.get(`/donors/${id}`),

  // Create new donor
  create: (donorData) => api.post('/donors', donorData),

  // Update donor
  update: (id, donorData) => api.put(`/donors/${id}`, donorData),

  // Delete donor (soft delete)
  delete: (id) => api.delete(`/donors/${id}`),

  // Get audit history for donor
  getAuditHistory: (id) => api.get(`/donors/${id}/audit-history`),

  // Import donors from CSV (initial pass - detects duplicates)
  importInitial: (formData) => api.post('/donors/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),

  // Resolve duplicate donors and complete import
  importResolve: (resolutionData) => api.post('/donors/import/resolve', { resolutions: resolutionData })
};

// Grants API Service
export const grantsService = {
  // Get all grants with filtering
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.includeDeleted) queryParams.append('includeDeleted', params.includeDeleted);

    return api.get(`/grants?${queryParams.toString()}`);
  },

  // Get single grant
  get: (id) => api.get(`/grants/${id}`),

  // Create new grant
  create: (grantData) => api.post('/grants', grantData),

  // Update grant
  update: (id, grantData) => api.put(`/grants/${id}`, grantData),

  // Delete grant (soft delete)
  delete: (id) => api.delete(`/grants/${id}`),

  // Get audit history
  getAuditHistory: (id) => api.get(`/grants/${id}/audit-history`)
};

// Donations API Service
export const donationsService = {
  // Get all donations with filtering
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.donor_id) queryParams.append('donor_id', params.donor_id);
    if (params.year) queryParams.append('year', params.year);
    if (params.minAmount) queryParams.append('minAmount', params.minAmount);
    if (params.maxAmount) queryParams.append('maxAmount', params.maxAmount);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.includeDeleted) queryParams.append('includeDeleted', params.includeDeleted);

    return api.get(`/donations?${queryParams.toString()}`);
  },

  // Get single donation
  get: (id) => api.get(`/donations/${id}`),

  // Create new donation
  create: (donationData) => api.post('/donations', donationData),

  // Update donation
  update: (id, donationData) => api.put(`/donations/${id}`, donationData),

  // Delete donation
  delete: (id) => api.delete(`/donations/${id}`),

  // Get audit history
  getAuditHistory: (id) => api.get(`/donations/${id}/audit-history`)
};

// Organizations API Service
export const organizationsService = {
  // Get all organizations
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.includeDeleted) queryParams.append('includeDeleted', params.includeDeleted);

    return api.get(`/organizations?${queryParams.toString()}`);
  },

  // Get single organization
  get: (id) => api.get(`/organizations/${id}`),

  // Create new organization
  create: (orgData) => api.post('/organizations', orgData),

  // Update organization
  update: (id, orgData) => api.put(`/organizations/${id}`, orgData),

  // Delete organization
  delete: (id) => api.delete(`/organizations/${id}`),

  // Get audit history
  getAuditHistory: (id) => api.get(`/organizations/${id}/audit-history`)
};

// Reports API Service
export const reportsService = {
  // Generate a report of specified type with optional filters
  generate: (reportType, filters = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    return api.get(`/reports/${reportType}?${queryParams.toString()}`);
  },

  // Get list of available report types
  getTypes: () => api.get('/reports/types'),

  // Export report as CSV
  exportCSV: (reportType, filters = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });
    return api.get(`/reports/${reportType}/export?${queryParams.toString()}`, {
      responseType: 'blob'
    });
  }
};

// Notes API Service
export const notesService = {
  // Get all notes for an entity
  list: (entityType, entityId) => {
    const queryParams = new URLSearchParams();
    queryParams.append('entity_type', entityType);
    queryParams.append('entity_id', entityId);
    return api.get(`/notes?${queryParams.toString()}`);
  },

  // Create a new note
  create: (entityType, entityId, noteData) => {
    return api.post('/notes', {
      entity_type: entityType,
      entity_id: entityId,
      note_text: noteData.note_text
    });
  },

  // Update a note
  update: (noteId, noteData) => {
    return api.put(`/notes/${noteId}`, noteData);
  },

  // Delete a note
  delete: (noteId) => {
    return api.delete(`/notes/${noteId}`)
  }
};

export default api;

